import { useEffect, useRef, useState, RefObject } from 'react';
import { ScreenSize, useGetScreenSize } from '../../../hooks';

const INTERACTIVE_PARENT_TAGS = ['BUTTON', 'A'];
const HOVER_DELAY_MS = 100;

export function useAnimatedIconHover(enabled: boolean): [RefObject<HTMLSpanElement | null>, boolean] {
    const ref = useRef<HTMLSpanElement>(null);
    const hoverTimer = useRef<number | null>(null);
    const touchTimer = useRef<number | null>(null);
    const [isHovered, setHovered] = useState(false);

    const screenSize = useGetScreenSize();
    const isTouchDevice = screenSize === ScreenSize.Mobile || screenSize === ScreenSize.Tablet;

    useEffect(() => {
        if (!enabled || !ref.current) {
            return;
        }

        const parent = ref.current.parentElement;

        if (!(parent && INTERACTIVE_PARENT_TAGS.includes(parent.tagName))) {
            return;
        }

        if (isTouchDevice) {
            const onTouchStart = () => {
                // Очищаем предыдущий таймер, если он есть
                if (touchTimer.current) {
                    clearTimeout(touchTimer.current);
                    touchTimer.current = null;
                }
                setHovered(true);
            };

            const onTouchEnd = () => {
                // Сбрасываем состояние после задержки, чтобы анимация успела отработать
                touchTimer.current = window.setTimeout(() => {
                    setHovered(false);
                    touchTimer.current = null;
                }, 300);
            };

            parent.addEventListener('touchstart', onTouchStart, { passive: true });
            parent.addEventListener('touchend', onTouchEnd, { passive: true });
            parent.addEventListener('mousedown', onTouchStart);
            parent.addEventListener('mouseup', onTouchEnd);

            return () => {
                // Очищаем таймер при размонтировании
                if (touchTimer.current) {
                    clearTimeout(touchTimer.current);
                    touchTimer.current = null;
                }

                parent.removeEventListener('touchstart', onTouchStart);
                parent.removeEventListener('touchend', onTouchEnd);
                parent.removeEventListener('mousedown', onTouchStart);
                parent.removeEventListener('mouseup', onTouchEnd);
            };
        }

        // Для десктопа оставляем логику с ховером
        const onEnter = () => {
            if (hoverTimer.current) {
                clearTimeout(hoverTimer.current);
            }
            hoverTimer.current = window.setTimeout(() => setHovered(true), HOVER_DELAY_MS);
        };

        const onLeave = () => {
            if (hoverTimer.current) {
                clearTimeout(hoverTimer.current);
            }
            setHovered(false);
        };

        parent.addEventListener('mouseenter', onEnter);
        parent.addEventListener('mouseleave', onLeave);

        return () => {
            if (hoverTimer.current) {
                clearTimeout(hoverTimer.current);
            }

            parent.removeEventListener('mouseenter', onEnter);
            parent.removeEventListener('mouseleave', onLeave);
        };
    }, [enabled, isTouchDevice]);

    return [ref, isHovered];
}
